import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import StarIcon from '@mui/icons-material/Star';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const gradientBackground = 'radial-gradient(circle at 10% 20%, rgba(255, 235, 245, 0.7), transparent 55%), radial-gradient(circle at 85% 15%, rgba(226, 232, 255, 0.6), transparent 45%), linear-gradient(135deg, #fff5f7 0%, #f9f6ff 50%, #f0f9ff 100%)';
const supportUrl = process.env.REACT_APP_SUPPORT_URL || 'mailto:support@promopeek.com';
const demoUrl = process.env.REACT_APP_DEMO_URL || 'https://abwrites.blog/';
const waitlistEndpoint = 'https://formspree.io/f/mnnojkyn';

export default function LoginPage({ onLogin, error }) {
  const currentYear = new Date().getFullYear();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState(null);

  const waitlistCtaEnabled = false;

  const handleOpenWaitlist = () => {
    setWaitlistStatus(null);
    setWaitlistOpen(true);
  };

  const handleCloseWaitlist = () => {
    setWaitlistOpen(false);
    setWaitlistStatus(null);
  };

  const handleWaitlistEmailChange = (event) => {
    setWaitlistEmail(event.target.value);
  };

  const handleSubmitWaitlist = async (event) => {
    event.preventDefault();
    const email = waitlistEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setWaitlistStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setWaitlistSubmitting(true);
    setWaitlistStatus(null);
    try {
      const response = await fetch(waitlistEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error('Failed to submit waitlist');
      }
      setWaitlistStatus({ type: 'success', message: "You're on the waitlist!🐹 We'll reach out soon." });
      setWaitlistEmail('');
    } catch (err) {
      setWaitlistStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const heroTrustPoints = useMemo(() => ['Promotions only', 'Read-only Access', 'Delete anytime'], []);

  const howItWorks = useMemo(
    () => [
      {
        title: 'Connect securely',
        description: 'Grant read-only access to your Promotions tab via Google OAuth.',
        icon: ShieldIcon,
      },
      {
        title: 'We scan Promotions',
        description: "Our system finds discount codes, expiry dates, and merchant details.",
        icon: MarkEmailReadIcon,
      },
      {
        title: 'Your dashboard appears',
        description: 'View all your deals in a clean and organized dashboard.',
        icon: DashboardCustomizeIcon,
      },
    ],
    []
  );

  const howItWorksPalettes = useMemo(
    () => [
      {
        background: 'linear-gradient(135deg, rgba(236,72,153,0.18), rgba(244,114,182,0.18))',
        border: '1px solid rgba(236,72,153,0.35)',
        color: '#db2777',
        shadow: '0 18px 32px rgba(236,72,153,0.18)',
      },
      {
        background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(14,165,233,0.18))',
        border: '1px solid rgba(59,130,246,0.35)',
        color: '#2563eb',
        shadow: '0 18px 32px rgba(59,130,246,0.18)',
      },
      {
        background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(236,72,153,0.18))',
        border: '1px solid rgba(168,85,247,0.35)',
        color: '#7c3aed',
        shadow: '0 18px 32px rgba(168,85,247,0.18)',
      },
    ],
    []
  );

  const features = useMemo(
    () => [
      {
        emoji: '🎁',
        title: 'Dig Up Deals',
        description: "We pull out codes you can't Google—personalized promos buried in your inbox.",
        accent: {
          border: 'rgba(236,72,153,0.2)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(254,242,255,0.9))',
          chipBg: 'rgba(236,72,153,0.15)',
          chipColor: '#be185d',
          avatarBg: 'rgba(236,72,153,0.18)',
          avatarColor: '#db2777',
        },
        example: {
          type: 'promo',
          brand: "Macy's",
          discount: '40% off',
          expiry: 'Expires in 18h',
          code: 'LXGRP8N7X7KV',
          badge: 'Personalized',
          note: 'Not a generic code. Only found in your Promotions tab.',
        },
        caption: 'Surfacing the one-off codes brands send only to you.',
      },
      {
        emoji: '⏰',
        title: 'Beat the Clock',
        description: "We flag expiring codes so you don't miss the savings window.",
        accent: {
          border: 'rgba(249,115,22,0.22)',
          background: 'linear-gradient(135deg, rgba(255,247,237,0.92), rgba(254,226,226,0.92))',
          chipBg: 'rgba(249,115,22,0.18)',
          chipColor: '#9a3412',
          avatarBg: 'rgba(249,115,22,0.18)',
          avatarColor: '#ea580c',
        },
        example: {
          type: 'promo',
          brand: 'Gap',
          discount: '50% off',
          expiry: 'Expires in 12h',
          code: 'GAP50',
          badge: 'Expiring soon',
          note: 'Expiry labels shown for each code.',
        },
        caption: 'Urgency badges make time-sensitive deals easy to spot.',
      },
      {
        emoji: '⚡',
        title: 'Copy Codes in 1 Click',
        description: 'Copy right from PromoPeek. Paste at checkout—done.',
        accent: {
          border: 'rgba(59,130,246,0.22)',
          background: 'linear-gradient(135deg, rgba(239,246,255,0.92), rgba(219,234,254,0.92))',
          chipBg: 'rgba(59,130,246,0.18)',
          chipColor: '#1d4ed8',
          avatarBg: 'rgba(59,130,246,0.18)',
          avatarColor: '#2563eb',
        },
        example: {
          type: 'promo',
          brand: 'Adidas',
          discount: '25% off',
          expiry: 'Ready to use',
          code: 'ADIDAS-SAVE25',
          note: 'No hunting, no typos—just copy and paste.',
        },
        caption: 'Codes stay copy-ready so you can apply them instantly.',
      },
      {
        emoji: '🔍',
        title: 'Stay Organized',
        description: 'Search by brand or discount. Filter to what matters most to you.',
        accent: {
          border: 'rgba(99,102,241,0.22)',
          background: 'linear-gradient(135deg, rgba(233,213,255,0.9), rgba(219,234,254,0.9))',
          chipBg: 'rgba(99,102,241,0.18)',
          chipColor: '#4338ca',
          avatarBg: 'rgba(99,102,241,0.18)',
          avatarColor: '#5b21b6',
        },
        example: {
          type: 'search',
          query: 'brand:Nike',
          helper: 'Search by brand or discount to zero in fast.',
          results: [
            { brand: 'Nike Factory Store', discount: '40% off', expiry: 'Expires in 12h', code: 'NIKE40FLASH' },
            { brand: 'Nike Members Exclusive', discount: '30% off', expiry: 'Expires in 1d', code: 'MEMBER30' },
          ],
        },
        caption: 'Find exactly what you need in seconds.',
      },
    ],
    []
  );

  const privacyHighlights = useMemo(
    () => [
      {
        icon: ShieldIcon,
        title: 'Read-only OAuth',
        detail: 'Secure Google OAuth scopes. Revoke anytime via Google Account → Security.',
      },
      {
        icon: MarkEmailReadIcon,
        title: 'Promotions tab only',
        detail: "We never touch Primary, Social, Updates, or Forums—Promotions is all we read.",
      },
      {
        icon: ContentCopyIcon,
        title: 'Minimal storage',
        detail: 'We temporarily store just code, merchant, expiry, and lightweight metadata to render your dashboard.',
      },
      {
        icon: AccessTimeIcon,
        title: '24-hour retention',
        detail: 'Everything purges automatically after 24 hours or immediately when you disconnect.',
      },
      {
        icon: LockOutlinedIcon,
        title: 'No selling or sharing',
        detail: 'Your data remains yours. No third-party sharing, no exceptions.',
      },
      {
        icon: DeleteOutlineIcon,
        title: 'Delete on demand',
        detail: 'Disconnect in one click through Google—your data disappears right away.',
      },
    ],
    []
  );

  const testimonials = useMemo(
    () => [
      {
        name: 'Laura R.',
        location: 'Miami, FL',
        quote:
          "PromoPeek found a personalized 40% off Macy's code I completely missed in my Promotions tab. I used it the same day and saved $38 on a jacket — honestly, it feels like free money hiding in my inbox.",
      },
      {
        name: 'Carlos H.',
        location: 'Austin, TX',
        quote:
          "I was skeptical about giving Gmail access, but the privacy section made it clear it only scans promotional emails. Within minutes, PromoPeek surfaced two expiring coupons that I wouldn't have seen otherwise.",
      },
      {
        name: 'Sumeet S.',
        location: 'Salt Lake City, UT',
        quote:
          "As someone who shops online a lot, PromoPeek keeps me organized. I can search by brand or discount, and copy codes straight to checkout. I've already saved over $100!",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        question: 'Do you read my personal emails?',
        answer: 'No. PromoPeek only scans the Promotions tab. We do not access Primary, Social, Updates, or Forums.',
      },
      {
        question: 'Why do you need Gmail access?',
        answer:
          'To uncover discount codes that only arrive via email and to display expiry information. Access is read-only and scoped to Promotions.',
      },
      {
        question: 'What exactly do you store? For how long?',
        answer:
          'We store the promo code, merchant, expiry timestamp, and minimal metadata needed to render your dashboard. Everything is purged automatically after 24 hours.',
      },
      {
        question: 'Can I delete my data and disconnect?',
        answer:
          'Yes. Google Account → Security → Third-party access to revoke PromoPeek. We immediately delete your cached data.',
      },
      {
        question: 'Is PromoPeek free?',
        answer: 'Yes. The base tier—including the last 24 hours of emails—is completely free.',
      },
      {
        question: 'Will this affect my Gmail?',
        answer: 'No. PromoPeek has read-only scopes. It cannot send, delete, or modify emails.',
      },
    ],
    []
  );

  const renderPromoCard = (data, accent, { compact = false } = {}) => (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        border: `1px solid ${accent?.border || 'rgba(148,163,184,0.25)'}`,
        background: 'rgba(255,255,255,0.96)',
        p: compact ? 1.5 : 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 1 : 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              width: compact ? 40 : 44,
              height: compact ? 40 : 44,
              bgcolor: accent?.avatarBg || 'rgba(148,163,184,0.15)',
              color: accent?.avatarColor || '#0f172a',
            }}
          >
            <LocalOfferIcon sx={{ fontSize: compact ? 18 : 20 }} />
          </Avatar>
          <Box>
            <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 700, color: '#0f172a' }}>
              {data.brand}
            </Typography>
            {data.badge && !compact && (
              <Chip
                label={data.badge}
                size="small"
                sx={{
                  mt: 0.5,
                  bgcolor: accent?.chipBg || 'rgba(148,163,184,0.18)',
                  color: accent?.chipColor || '#475569',
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
        </Stack>
        {data.discount && (
          <Chip
            label={data.discount}
            size="small"
            sx={{
              bgcolor: accent?.chipBg || 'rgba(148,163,184,0.18)',
              color: accent?.chipColor || '#475569',
              fontWeight: 700,
            }}
          />
        )}
      </Stack>
      {data.expiry && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#475569' }}>
          <AccessTimeIcon sx={{ fontSize: compact ? 16 : 18 }} />
          <Typography variant="body2">{data.expiry}</Typography>
        </Stack>
      )}
      {data.code && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              flex: 1,
              bgcolor: 'rgba(241,245,249,0.8)',
              borderRadius: '9999px',
              px: compact ? 1.5 : 2,
              py: compact ? 0.75 : 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              color: '#0f172a',
              border: '1px solid rgba(148,163,184,0.25)',
              textAlign: 'center',
            }}
          >
            {data.code}
          </Box>
          <IconButton
            size="small"
            sx={{
              bgcolor: 'rgba(148,163,184,0.18)',
              color: '#475569',
              '&:hover': { bgcolor: 'rgba(148,163,184,0.28)' },
            }}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      )}
      {data.note && !compact && (
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {data.note}
        </Typography>
      )}
    </Paper>
  );

  const renderSearchExample = (data, accent) => (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        border: `1px solid ${accent?.border || 'rgba(148,163,184,0.25)'}`,
        background: 'rgba(255,255,255,0.96)',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: '9999px',
          px: 2.5,
          py: 1,
          bgcolor: 'rgba(99,102,241,0.12)',
          color: '#4338ca',
          fontWeight: 600,
          fontFamily: 'monospace',
          border: '1px solid rgba(99,102,241,0.25)',
          alignSelf: 'flex-start',
        }}
      >
        <FilterAltIcon sx={{ fontSize: 18 }} />
        {data.query}
      </Box>
      <Stack spacing={1.5}>
        {data.results?.map((result) => (
          <Box key={`${result.brand}-${result.code}`}>{renderPromoCard(result, accent, { compact: true })}</Box>
        ))}
      </Stack>
      {data.helper && (
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {data.helper}
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: gradientBackground, position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: '-20% 40% auto -10%',
          height: 480,
          filter: 'blur(120px)',
          background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.28), rgba(99, 102, 241, 0.18))',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: '60% -10% -15% auto',
          width: 360,
          height: 360,
          borderRadius: '50%',
          filter: 'blur(120px)',
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(236, 72, 153, 0.25))',
          zIndex: 0,
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 6, md: 10 } }}>
        {/* Hero */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 3.5,
            mb: { xs: 10, md: 12 },
            maxWidth: 720,
            mx: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src="/images/promopeak-logo.png"
                alt="PromoPeek Logo"
                sx={{ width: 108, height: 108, borderRadius: 4, boxShadow: '0 18px 40px rgba(236,72,153,0.25)' }}
              />
              <AutoAwesomeIcon sx={{ position: 'absolute', top: -8, right: -8, color: '#facc15', width: 34, height: 34 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(90deg,#ec4899,#a855f7)',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                letterSpacing: 1.5,
                fontSize: { xs: 20, md: 24 },
              }}
            >
              PromoPeek
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: 36, md: 52 }, lineHeight: 1.1 }}>
            Inbox deals, organized.
          </Typography>
          <Typography variant="h6" sx={{ color: '#475569', fontWeight: 500, maxWidth: 520 }}>
            Read-only access. Promotions only. Nothing sold. Ever.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button
              onClick={onLogin}
              size="large"
              startIcon={<MailOutlineIcon />}
              sx={{
                borderRadius: '9999px',
                px: 3.5,
                py: 1.75,
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #ff68e0 0%, #907bff 100%)',
                color: '#fff',
                boxShadow: '0 18px 35px rgba(236,72,153,0.35)',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff50d8 0%, #7c63ff 100%)',
                  boxShadow: '0 20px 40px rgba(147,197,253,0.35)',
                },
              }}
            >
              Connect your Gmail — it's free
            </Button>
            {waitlistCtaEnabled && (
              <Button
                onClick={handleOpenWaitlist}
                size="large"
                startIcon={<MailOutlineIcon />}
                sx={{
                  borderRadius: '9999px',
                  px: 3.5,
                  py: 1.75,
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #ff68e0 0%, #907bff 100%)',
                  color: '#fff',
                  boxShadow: '0 18px 35px rgba(236,72,153,0.35)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff50d8 0%, #7c63ff 100%)',
                    boxShadow: '0 20px 40px rgba(147,197,253,0.35)',
                  },
                }}
              >
                Join the Waitlist — it's free
              </Button>
            )}
            <Button
              href={demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="large"
              startIcon={<PlayCircleFilledWhiteIcon />}
              variant="outlined"
              sx={{
                borderRadius: '9999px',
                px: 3.5,
                py: 1.75,
                fontWeight: 700,
                fontSize: '1rem',
                color: '#5b21b6',
                borderColor: 'rgba(139, 92, 246, 0.35)',
                backdropFilter: 'blur(6px)',
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                '&:hover': {
                  borderColor: 'rgba(139, 92, 246, 0.55)',
                  backgroundColor: 'rgba(255,255,255,1)',
                },
              }}
            >
              Watch 30-second demo
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            {heroTrustPoints.map((label) => (
              <Stack key={label} direction="row" spacing={1} alignItems="center">
                <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                  {label}
                </Typography>
              </Stack>
            ))}
          </Stack>
          {error && (
            <Alert severity="error" sx={{ maxWidth: 480, width: '100%' }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* How it works */}
        <Box sx={{ mb: { xs: 10, md: 12 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center', mb: 5 }}>
            How it works
          </Typography>
          <Grid container spacing={3.5}>
            {howItWorks.map(({ title, description, icon: Icon }, idx) => {
              const palette = howItWorksPalettes[idx] || howItWorksPalettes[howItWorksPalettes.length - 1];
              return (
                <Grid item xs={12} md={4} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid rgba(236,72,153,0.18)',
                    background: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(16px)',
                    p: 0.5,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 24px 40px rgba(15,23,42,0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3, alignItems: 'center', textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        background: palette.background,
                        border: palette.border,
                        color: palette.color,
                        boxShadow: palette.shadow,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {idx + 1}. {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      {description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
            })}
          </Grid>
        </Box>

        {/* Features */}
        <Box sx={{ mb: { xs: 10, md: 12 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center', mb: 5 }}>
            How PromoPeek helps you save
          </Typography>
          <Grid container spacing={3.5}>
            {features.map(({ emoji, title, description, example, caption, accent }) => (
              <Grid item xs={12} md={6} key={title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${accent.border}`,
                    background: accent.background,
                    backdropFilter: 'blur(18px)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Stack direction="row" spacing={2.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '26%',
                          background: 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(111,114,255,0.2))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 28,
                        }}
                      >
                        {emoji}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                          {description}
                        </Typography>
                      </Box>
                    </Stack>
                    {example.type === 'search'
                      ? renderSearchExample(example, accent)
                      : renderPromoCard(example, accent)}
                    <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                      {caption}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Privacy & Security */}
        <Box sx={{ mb: { xs: 10, md: 12 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Stack spacing={1.5} alignItems="center" maxWidth={620}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Privacy & Security
              </Typography>
              <Typography variant="body1" sx={{ color: '#475569' }}>
                Trust is our north star. PromoPeek is transparent about what we read, what we store, and how quickly you can make it disappear.
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              {privacyHighlights.map(({ icon: Icon, title, detail }, idx) => (
                <Grid item xs={12} sm={6} md={4} key={title}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(236,72,153,0.18)',
                      background: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 3, alignItems: 'center', textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 52,
                          height: 52,
                          background:
                            idx % 3 === 0
                              ? 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(244,114,182,0.2))'
                              : idx % 3 === 1
                              ? 'linear-gradient(135deg, rgba(59,130,246,0.2), rgba(14,165,233,0.2))'
                              : 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(236,72,153,0.2))',
                          color: idx % 3 === 0 ? '#db2777' : idx % 3 === 1 ? '#2563eb' : '#7c3aed',
                          boxShadow: '0 12px 24px rgba(15,23,42,0.12)',
                        }}
                      >
                        <Icon />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {detail}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
              <Button
                href="/privacy"
                variant="outlined"
                sx={{
                  borderRadius: '9999px',
                  borderColor: 'rgba(139, 92, 246, 0.35)',
                  color: '#5b21b6',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3,
                  py: 1.25,
                }}
              >
                View Privacy Policy
              </Button>
              <Button
                href={supportUrl}
                variant="outlined"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: '9999px',
                  borderColor: 'rgba(139, 92, 246, 0.35)',
                  color: '#5b21b6',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3,
                  py: 1.25,
                  '&:hover': {
                    borderColor: 'rgba(139, 92, 246, 0.55)',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                  },
                }}
              >
                Contact Us
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Social Proof */}
        <Box sx={{ mb: { xs: 10, md: 12 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center', mb: 5 }}>
            What users are saying
          </Typography>
          <Grid container spacing={3.5}>
            {testimonials.map(({ name, location, quote }) => (
              <Grid item xs={12} md={4} key={name}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid rgba(236,72,153,0.18)',
                    background: 'rgba(255,255,255,0.88)',
                    backdropFilter: 'blur(14px)',
                    p: 1,
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction="row" spacing={0.5}>
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <StarIcon key={idx} sx={{ fontSize: 18, color: '#facc15' }} />
                      ))}
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.7 }}>
                      “{quote}”
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {location}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

        </Box>

        {/* FAQ */}
        <Box sx={{ mb: { xs: 10, md: 12 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center', mb: 5 }}>
            Frequently asked questions
          </Typography>
          <Card
            elevation={0}
            sx={{
              maxWidth: 720,
              mx: 'auto',
              borderRadius: 4,
              border: '1px solid rgba(236,72,153,0.2)',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(14px)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={1.5}>
                {faqs.map(({ question, answer }) => (
                  <AccordionItem key={question} question={question} answer={answer} />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Dialog open={waitlistOpen} onClose={handleCloseWaitlist} maxWidth="xs" fullWidth>
          <Box component="form" onSubmit={handleSubmitWaitlist}>
            <DialogTitle sx={{ fontWeight: 700 }}>Join the Waitlist</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Drop your email and we&apos;ll let you know the moment PromoPeek opens up.
              </Typography>
              {waitlistStatus && (
                <Alert severity={waitlistStatus.type} onClose={() => setWaitlistStatus(null)}>
                  {waitlistStatus.message}
                </Alert>
              )}
              <TextField
                label="Email"
                type="email"
                value={waitlistEmail}
                onChange={handleWaitlistEmailChange}
                autoFocus
                fullWidth
                disabled={waitlistSubmitting}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={handleCloseWaitlist} disabled={waitlistSubmitting} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={waitlistSubmitting}
                sx={{
                  borderRadius: '9999px',
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#fff',
                  background: 'linear-gradient(135deg, #ff68e0 0%, #907bff 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff50d8 0%, #7c63ff 100%)',
                  },
                }}
              >
                {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pb: { xs: 4, md: 6 } }}>
          <Button
            onClick={onLogin}
            startIcon={<MailOutlineIcon />}
            sx={{
              borderRadius: '9999px',
              px: 5,
              py: 1.75,
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              color: '#fff',
              background: 'linear-gradient(135deg, #ff68e0 0%, #907bff 100%)',
              boxShadow: '0 18px 35px rgba(147,197,253,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff50d8 0%, #7c63ff 100%)',
                boxShadow: '0 20px 40px rgba(147,197,253,0.4)',
              },
            }}
          >
            Connect your Gmail — it's free
          </Button>
          {waitlistCtaEnabled && (
            <Button
              onClick={handleOpenWaitlist}
              startIcon={<MailOutlineIcon />}
              sx={{
                borderRadius: '9999px',
                px: 5,
                py: 1.75,
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                color: '#fff',
                background: 'linear-gradient(135deg, #ff68e0 0%, #907bff 100%)',
                boxShadow: '0 18px 35px rgba(147,197,253,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff50d8 0%, #7c63ff 100%)',
                  boxShadow: '0 20px 40px rgba(147,197,253,0.4)',
                },
              }}
            >
              Join the Waitlist — it's free
            </Button>
          )}

          <Stack direction="row" spacing={3} sx={{ color: '#475569', fontWeight: 600 }}>
            <Link href="/privacy" underline="none" sx={{ color: 'inherit', '&:hover': { color: '#ec4899' } }}>
              Privacy Policy
            </Link>
            <Link href={supportUrl} underline="none" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', '&:hover': { color: '#ec4899' } }}>
              Contact
            </Link>
          </Stack>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 1.25,
              borderRadius: '9999px',
              border: '1px solid rgba(236,72,153,0.2)',
              background: 'rgba(255,255,255,0.9)',
              color: '#475569',
              fontWeight: 500,
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: '#ec4899' }} />
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'inherit' }}>
              Made with love by your hamster friend 🐹 © {currentYear} PromoPeek
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function AccordionItem({ question, answer }) {
  return (
    <Accordion
      disableGutters
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(236,72,153,0.18)',
        background: 'rgba(255,255,255,0.85)',
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 2.5, md: 3 }, py: 1.75 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pb: 2.5 }}>
        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.7 }}>
          {answer}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
